import React, { useState } from 'react';
import { MessageSquare, Send, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import EmployeeAvatar from '../EmployeeAvatar';
import { Button } from '../ui/button';
import { useLocale, useT } from '../../contexts/LocaleProvider';

export default function CommentList({ comments = [], myEmail, canModerate, onPost, onDelete }) {
  const t = useT();
  const { intlLocale } = useLocale();
  const fmt = (iso) => new Intl.DateTimeFormat(intlLocale, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));

  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  // Auto-open when there are comments · readers shouldn't have to click first
  const expanded = open || comments.length > 0;

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      await onPost(text);
      setBody('');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const countLabel = comments.length === 0
    ? t('comments.comment')
    : t(comments.length > 1 ? 'comments.countMany' : 'comments.count', { n: comments.length });

  return (
    <div className="mt-3 pt-3 border-t border-[hsl(var(--rule))]">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {countLabel}
        </button>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-soft))]">
              <MessageSquare className="h-3.5 w-3.5" />
              {t(comments.length > 1 ? 'comments.countMany' : 'comments.count', { n: comments.length })}
            </div>
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] inline-flex items-center gap-1"
            >
              {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {open ? t('comments.collapse') : t('comments.expand')}
            </button>
          </div>

          {comments.length > 0 && (
            <ul className="space-y-3 mb-3">
              {comments.map(c => {
                const mine = (c.author_email || '').toLowerCase() === (myEmail || '').toLowerCase();
                const canDelete = mine || canModerate;
                return (
                  <li key={c.id} className="flex items-start gap-2.5">
                    <EmployeeAvatar fullName={c.author_full_name} email={c.author_email} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[13px] font-medium">{c.author_full_name || c.author_email}</span>
                        <span className="text-[11px] text-[hsl(var(--text-muted))]">{fmt(c.created_at)}</span>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => onDelete(c.id)}
                            className="ml-auto text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]"
                            aria-label={t('common.delete')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <div className="text-[14px] whitespace-pre-wrap leading-snug mt-0.5">{c.body}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex items-end gap-2">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={1}
              placeholder={t('comments.placeholder')}
              className="input flex-1 min-h-[40px] resize-none"
              onKeyDown={(e) => {
                // Enter submits · Shift+Enter (and IME composition) keeps
                // the default newline behaviour for multi-line comments.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <Button size="sm" onClick={submit} disabled={busy || !body.trim()} className="gap-1.5 shrink-0">
              <Send className="h-3.5 w-3.5" />
              {t('common.send')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
